
import React, { useState } from 'react';

const Footer = () => {
  // Current year for the copyright line
  const currentYear = new Date().getFullYear();

  // Newsletter form state
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  // Footer navigation links
  const footerLinks = {
    services: [
      'Software Licensing',
      'Implementation',
      'Integration Services',
      'Process Automation',
      'ITIL Consulting',
      'Training Programs',
    ],
    products: [
      'ManageEngine Suite',
      'AutomMate Platform',
      'Zoho One',
      'ServiceDesk Plus',
      'Endpoint Central',
      'OpManager',
    ],
    company: [
      'About Us',
      'Our Team',
      'Partnerships',
      'Careers',
      'Case Studies',
      'Blog',
    ],
    support: [
      'Contact Support',
      'Documentation',
      'Training Resources',
      'Community Forum',
      'Service Status',
      'Knowledge Base',
    ],
  };

  /** --------------------------------------------------------------------
   *  Newsletter submit handler
   *  --------------------------------------------------------------------
   *  Sends the entered e‑mail address to the Readdy endpoint.
   *  Handles success / error responses and provides user feedback.
   *  -------------------------------------------------------------------- */
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();

    // Basic client‑side validation
    if (!email.trim()) {
      setSubmitMessage('Lütfen e‑posta adresinizi girin.');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch(
        'https://readdy.ai/api/form/d4532tej3hkp5gme3f9g',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData.toString(),
        }
      );

      if (response.ok) {
        setSubmitMessage('Başarıyla abone oldunuz!');
        setEmail('');
      } else {
        setSubmitMessage('Bir hata oluştu. Lütfen tekrar deneyin.');
      }
    } catch (error) {
      console.error('Newsletter submit error:', error);
      setSubmitMessage('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-white border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        {/* ====================== Main Grid ====================== */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Company description + social icons */}
          <div className="lg:col-span-2">
            <h3
              className="text-3xl font-bold mb-4 text-blue-600"
              style={{ fontFamily: '"Pacifico", serif' }}
            >
              WOTECH
            </h3>
            <p className="text-gray-600 mb-6 leading-relaxed max-w-md">
              Leading technology consulting company specializing in ManageEngine,
              AutomMate, and Zoho One solutions. We help enterprises modernize
              their IT operations with comprehensive ITSM, ITOM, and business
              process automation.
            </p>
            <div className="flex space-x-4">
              <a
                href="#"
                className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center hover:bg-blue-100 transition-colors cursor-pointer group"
              >
                <i className="ri-linkedin-fill text-xl text-blue-600 group-hover:text-blue-700"></i>
              </a>
              <a
                href="#"
                className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center hover:bg-blue-100 transition-colors cursor-pointer group"
              >
                <i className="ri-youtube-fill text-xl text-blue-600 group-hover:text-blue-700"></i>
              </a>
              <a
                href="#"
                className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center hover:bg-blue-100 transition-colors cursor-pointer group"
              >
                <i className="ri-twitter-fill text-xl text-blue-600 group-hover:text-blue-700"></i>
              </a>
              <a
                href="#"
                className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center hover:bg-blue-100 transition-colors cursor-pointer group"
              >
                <i className="ri-facebook-fill text-xl text-blue-600 group-hover:text-blue-700"></i>
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-gray-900">
              Services
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link, index) => (
                <li key={index}>
                  <a
                    href="/services"
                    className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer text-sm"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-gray-900">
              Products
            </h4>
            <ul className="space-y-3">
              {footerLinks.products.map((link, index) => (
                <li key={index}>
                  <a
                    href="/products"
                    className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer text-sm"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-gray-900">
              Support
            </h4>
            <ul className="space-y-3">
              {footerLinks.support.map((link, index) => (
                <li key={index}>
                  <a
                    href="/contact"
                    className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer text-sm"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ====================== Newsletter Section ====================== */}
        <div className="mt-16 pt-12 border-t border-gray-100">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Stay Updated
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Get the latest insights on IT automation, digital transformation,
              and technology trends delivered to your inbox.
            </p>

            <form
              onSubmit={handleNewsletterSubmit}
              className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto"
            >
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                required
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Gönderiliyor...' : 'Subscribe'}
              </button>
            </form>

            {/* Feedback message */}
            {submitMessage && (
              <p
                className={`mt-2 text-sm ${
                  submitMessage.includes('Başarıyla')
                    ? 'text-green-500'
                    : 'text-red-500'
                }`}
              >
                {submitMessage}
              </p>
            )}
          </div>
        </div>

        {/* ====================== Bottom Bar ====================== */}
        <div className="mt-12 pt-8 border-t border-gray-100">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
            <div className="flex flex-col sm:flex-row items-center gap-6 text-sm text-gray-500">
              <p>
                &copy; {currentYear} WOTECH Information Technologies. All rights
                reserved.
              </p>
              <div className="flex items-center space-x-6">
                <a
                  href="/privacy"
                  className="hover:text-blue-600 transition-colors cursor-pointer"
                >
                  Privacy Policy
                </a>
                <a
                  href="/terms"
                  className="hover:text-blue-600 transition-colors cursor-pointer"
                >
                  Terms of Service
                </a>
                <a
                  href="/cookie"
                  className="hover:text-blue-600 transition-colors cursor-pointer"
                >
                  Cookie Policy
                </a>
              </div>
            </div>

            <div className="flex items-center space-x-6">
              <a
                href="https://wotech.com.tr"
                className="text-gray-500 hover:text-blue-600 transition-colors cursor-pointer text-sm"
              >
                Powered by WOTECH
              </a>
            </div>
          </div>
        </div>

        {/* ====================== Certifications ====================== */}
        <div className="mt-8 pt-8 border-gray-100">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center space-x-8 text-sm text-gray-500">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <i className="ri-shield-check-line text-green-600"></i>
                </div>
                <span>ISO 27001 Certified</span>
              </div>

              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <i className="ri-award-line text-blue-600"></i>
                </div>
                <span>ITIL Aligned</span>
              </div>
            </div>

            <div className="text-sm text-gray-500">
              <span className="font-medium text-gray-700">
                Authorized Partner:{' '}
              </span>
              <span className="text-blue-600 font-medium">
                ManageEngine • AutomMate • Zoho One
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
